// @ts-check

/**
 * @typedef {import("../types.js").Product} Product
 */

import { productEntries, productCategories } from "../gql/productSelectorQueries.js";
import { debounce, split } from "lodash";
import { scrollTo } from '../utilities/_scrollTo';

export default function productSelector() {
	return {
		/** @type {Object[]} */
		categories: [],

		/** @type {Product[]} */
		entries: [],

	  /** @type {{ id: string; title: string; childrenIds: (string|number)[] }[]} productTypeParent */
		productTypeParent: [], // Selected product type parent(s)

    /** @type {(string|number)[]} productType */
		productType: [], // Selected product type(s)

    /** @type {(string|number)[]} productColourRange */
		productColourRange: [], // Selected product colour range(s)

		/** @type {(string|number)[]} productColour */
		productColour: [], // Selected product colour(s)

		/** @type {(string|number)[]} productFinish */
		productFinish: [], // Selected product finish(es)

		/** @type {(string|number)[]} productTexture */
		productTexture: [], // Selected product texture(s)

		/** @type {number} page */
		page: 1, // Current page

		/** @type {number} perPage */
		perPage: 15, // Number of entries per page

		/** @type {string} search string */
		productSearch: "",

		/** @type {boolean} */
		loading: false, // true when loading data

		/** @type {HTMLInputElement[]} */
		filterEls: [],

		async init() {
			// Get the filter elements
			this.filterEls = this.$el.querySelectorAll("[data-filter]");

			// Set the selected filters from the URL
			const url = new URL(window.location.toString());

      this.productType = url.searchParams.get("productType")
				? split(url.searchParams.get("productType"), ",")
				: [];
      this.productColourRange = url.searchParams.get("productColourRange")
				? split(url.searchParams.get("productColourRange"), ",")
				: [];
			this.productColour = url.searchParams.get("productColour")
				? split(url.searchParams.get("productColour"), ",")
				: [];
			this.productFinish = url.searchParams.get("productFinish")
				? split(url.searchParams.get("productFinish"), ",")
				: [];
			this.productTexture = url.searchParams.get("productTexture")
				? split(url.searchParams.get("productTexture"), ",")
				: [];
			this.productSearch = url.searchParams.get("productSearch") || "";

			const pageParam = url.searchParams.get("page");
			this.page = pageParam ? +pageParam : 1;

			// Wait for data to be fetched
			await Promise.all([this.getProductEntries(), this.getProductCategories()]);

			// Do not allow the user to select filters while loading
			this.$watch("loading", (loading) => {
				if (loading) {
					this.filterEls.forEach((el) => {
						el.disabled = true;
					});

          const filterSelectedItems = document.querySelector('.filters__selected__items')
          if (filterSelectedItems !== null) {
            filterSelectedItems.style.width = '100%'
            filterSelectedItems.classList.add('is-loading')
          }
				} else {
					this.filterEls.forEach((el) => {
						// Keep track of the disabled state using a data attrib
						// So that we don't re-enable filters that were disabled by null results
						if (!el.dataset.disabled) {
							el.disabled = false;
						}
					});
				}
			});

      // Check for disabled filter menus
      //
      const self = this
      setTimeout(function() {
        self.checkAllDisabledFilterMenus()
        self.handleProductTypeParent()
      }, 500)

			// Watch for changes to search, brickColour, brickFinish, brickType
			// Debounce to prevent multiple calls
			this.$watch(
				"productSearch",
				debounce(() => {
					this.handleFilterChange();

          if (this.productSearch !== '' && window.innerWidth < 992) {
            this.scrollToResults();
          }
				}, 1250)
			);

      this.$watch("productType", () => {
				this.loading = true;
				debounce(() => {
					this.handleFilterChange();
				}, 320)();
			});

      this.$watch("productColourRange", () => {
				this.loading = true;
				debounce(() => {
					this.handleFilterChange();
				}, 320)();
			});

			this.$watch("productColour", () => {
				this.loading = true;
				debounce(() => {
					this.handleFilterChange();
				}, 320)();
			});

			this.$watch("productFinish", () => {
				this.loading = true;
				debounce(() => {
					this.handleFilterChange();
				}, 320)();
			});

			this.$watch("productTexture", () => {
				this.loading = true;
				debounce(() => {
					this.handleFilterChange();
				}, 320)();
			});

			// watch for changes to page and replace the history state
			this.$watch("page", () => {
				this.replaceHistoryState();
			});
		},

		/**
		 * Reset filters
		 */
		resetFilters() {
      this.productTypeParent = [];
      this.productType = [];
      this.productColourRange = [];
			this.productColour = [];
			this.productFinish = [];
			this.productTexture = [];
			this.productSearch = "";
      this.page = 1;

      // Remove load state
      //
      const filterSelectedItems = document.querySelector('.filters__selected__items')
      if (filterSelectedItems !== null) {
        filterSelectedItems.style.width = ''
        filterSelectedItems.classList.remove('is-loading')
      }
      
			this.getProductEntries();
			this.replaceHistoryState();
		},

		/**
		 * Reset filter by ID
		 */
		resetFilter(id) {
      this.productType = this.productType.filter((item) => item !== id);
      this.productColourRange = this.productColourRange.filter((item) => item !== id);
			this.productColour = this.productColour.filter((item) => item !== id);
			this.productFinish = this.productFinish.filter((item) => item !== id);
			this.productTexture = this.productTexture.filter((item) => item !== id);
		},

    /**
     * Reset multiple filters by IDs
     * @param {(string|number)[]} ids
     */
    resetMultipleFilters(ids) {
      ids.forEach(id => {
        this.productType = this.productType.filter((item) => item !== id);
        this.productColourRange = this.productColourRange.filter((item) => item !== id);
        this.productColour = this.productColour.filter((item) => item !== id);
        this.productFinish = this.productFinish.filter((item) => item !== id);
        this.productTexture = this.productTexture.filter((item) => item !== id);
      });
    },

		/**
		 * handleFilterChange
		 */
		handleFilterChange() {
      const filterSelectedItems = document.querySelector('.filters__selected__items')
      if (filterSelectedItems !== null) {
        filterSelectedItems.style.width = '100%'
        filterSelectedItems.classList.add('is-loading')
        
        setTimeout(function() {
          filterSelectedItems.style.width = ''
          filterSelectedItems.classList.remove('is-loading')
        }, 800)
      }

			this.page = 1;
			this.getProductEntries();
      this.getProductCategories();
			this.replaceHistoryState();
      this.handleProductTypeParent();
		},

		/**
		 * replaceHistoryState
		 */
		replaceHistoryState() {
			const url = new URL(window.location.toString());
      url.searchParams.set("productType", this.productType.toString());
      url.searchParams.set("productColourRange", this.productColourRange.toString());
			url.searchParams.set("productColour", this.productColour.toString());
			url.searchParams.set("productFinish", this.productFinish.toString());
			url.searchParams.set("productTexture", this.productTexture.toString());
			url.searchParams.set("productSearch", this.productSearch);
			url.searchParams.set("page", this.page.toString());
			window.history.replaceState({}, "", url);
		},

		/**
		 * getProductEntries
		 */
		async getProductEntries() {
			// use fetch to run the productEntries query with variables
			// and return the results as JSON
			this.loading = true;

			return fetch("/api", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					query: productEntries,
					variables: {
            productType: this.productTypeQuery,
						relations: this.relations,
						term: this.productSearch,
					},
				}),
			})
				.then((response) => response.json())
				.then(({ data }) => {
					// Set the entries to the returned data
					this.entries = data.entries;
				})
				.finally(() => {
					this.loading = false;

          // Check for disabled filter menus
          //
          const self = this
          setTimeout(function() {
            self.checkAllDisabledFilterMenus()
          }, 500)
				});
		},

		/**
		 * getProductCategories
		 */
		async getProductCategories() {
			// use fetch to run the brickCategories query
			// and return the results as JSON
			return fetch("/api", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					query: productCategories,
				}),
			})
				.then((response) => response.json())
				.then(({ data }) => {
					// Spread and merge the categories into a single array
					this.categories = [
            ...data.productType,
            ...data.colourRange,
						...data.colour,
						...data.finish,
						...data.texture,
					];          
				});
		},

    /**
		 * scroll to results 
		 */
    scrollToResults() {
      const resultsElement = document.querySelector('#results');
      const headerHeight = document.querySelector('#header').clientHeight + 20;
      scrollTo(resultsElement, -headerHeight);
    },

		/**
		 * isOptionFound
		 * @param {string} option
		 * @param {number} targetId
		 * @returns {boolean}
		 */
		isOptionFound(option, targetId) {
			for (let entry of this.entries) {
				for (let row of entry[option]) {

					if (+row.id === targetId) {
						return true;
					}
				}
			}
			return false;
		},

    /**
		 * productTypeQuery
		 */
		get productTypeQuery() {
      if (!this.productType.length) {
				return null;
			} else {
				// Otherwise, return the selected filters

				return [
					"or",
          ...this.productType
				];
			}
    },

		/**
		 * relations
		 */
		get relations() {
			if (
				// If no filters are selected, return null
        !this.productColourRange.length &&
				!this.productColour.length &&
				!this.productFinish.length &&
				!this.productTexture.length
			) {
				return null;
			} else {
				// Otherwise, return the selected filters

				return [
					"and",
          ...this.productColourRange,
					...this.productColour,
					...this.productFinish,
					...this.productTexture,
				];
			}
		},

		/**
		 * paginatedEntries
		 */
		get paginatedEntries() {
			// Calculate the start and end entries for the current page
			const start = (this.page - 1) * this.perPage;
			const end = start + this.perPage;

			// Return the entries for the current page
			return this.entries.slice(start, end);
		},

		/**
		 * pagination
		 */
		get pagination() {
			// Calculate the number of pages
			const pages = this.totalPages;

			// Create an array of pages
			const pagination = Array.from({ length: pages }, (_, i) => i + 1);

			// If there are more than 2 pages, filter out the pages that are not within 2 of the current page
			if (pages > 2) {
				return pagination.filter(
					(page) =>
						page === 1 ||
						page === this.page ||
						page === pages ||
						(page >= this.page - 2 && page <= this.page + 2)
				);
			} else {
				// Otherwise, return all the pages
				return pagination;
			}
		},

		/**
		 * total pages
		 */
		get totalPages() {
			// Calculate the number of pages
			return Math.ceil(this.entries.length / this.perPage);
		},

		/**
		 * @param {number} page
		 */
		goToPage(page) {
			// Set the current page
			this.page = page;

			// Scroll to #results
			const results = document.querySelector("#results");
			if (results) {
				results.scrollIntoView({
					behavior: "smooth",
				});
			}
		},

		/**
     * removeAll
     */
    removeAll() {
      // Clear the shortlist in the store
      this.$store.productShortlist.shortlist = [];
    },

    /**
       * Filter shown check
       */
    filterShownCheck(parentTitle) {
      // Check if a productTypeParent exists with the matching title
      const parentExists = this.productTypeParent.some(parent => parent.title === parentTitle);

      // Return false if a matching parent is found, otherwise return true
      return !parentExists;
    },

    /**
		 * Handle type
		 */
    handleTypeClick(event) {
      // Prefer currentTarget if your click is bound on the DIV itself
      // event.target can sometimes be a child element (like an <img> or <p>)
      const target = event.currentTarget;

      // Parse the JSON array of IDs from data-ids
      const ids = JSON.parse(target.dataset.ids);

      // Clear all types
      this.productType = []

      // Set loading state well before ids are added
      //
      const filterSelectedItems = document.querySelector('.filters__selected__items')
      if (filterSelectedItems !== null) {
        filterSelectedItems.style.width = '100%'
        filterSelectedItems.classList.add('is-loading')
      }

      // If already active, remove from productType
      if (target.classList.contains('is-active')) {
        target.classList.remove('is-active');

        // Remove each ID from productType
        // Remember to convert both sides to the same type (string or number)
        ids.forEach((id) => {
          this.productType = this.productType.filter((item) => String(item) !== String(id));
        });
      } 
      // Otherwise, add them to productType
      else {
        target.classList.add('is-active');

        // Push each ID only if it doesn’t already exist
        ids.forEach((id) => {
          if (!this.productType.includes(String(id))) {
            this.productType.push(String(id));
          }
        });
      }
    },

    /**
     * Determine if the current element should be "active"
     * by comparing data-ids to the productType array.
     * @param {string} idsStr - JSON string from data-ids
     * @returns {boolean}
     */
    isActiveParent(idsStr) {
      // Parse the JSON array of children IDs
      const ids = JSON.parse(idsStr);

      // Make sure you match types (string/number) correctly:
      return ids.every(id => this.productType.includes(String(id)));
    },

    /**
     * Remove parent type
     */
    removeParentType(item) {
      // Remove each ID from productType
      item.childrenIds.forEach((id) => {
        this.productType = this.productType.filter((item) => String(item) !== String(id));
      });
    },

    /**
     * Handle product type parent
     */
    handleProductTypeParent() {
      // Reset productTypeParent
      this.productTypeParent = [];    

      // Iterate over each element with the class "productSelector__type"
      document.querySelectorAll('.productSelector__type').forEach((element) => {
        const parentId = element.dataset.id;
        const childrenIds = JSON.parse(element.dataset.ids);

        // Check if all children IDs exist in productType
        const allChildrenExist = childrenIds.every(id => this.productType.includes(String(id)));

        if (allChildrenExist) {
          // Add the parent's id and title to productTypeParent array
          this.productTypeParent.push({
            id: parentId,
            title: element.dataset.title,
            childrenIds: childrenIds
          });
        } else {
          // Remove the parent's id from productTypeParent array
          const index = this.productTypeParent.findIndex(parent => parent.id === parentId);
          if (index !== -1) {
            this.productTypeParent.splice(index, 1);
          }
        }
      });
    },

    /**
     * Check if a parent type exists for the given id
     * @param {string | number} id
     * @returns {boolean}
     */
    checkParentType(id) {
      const idStr = String(id);
      const exists = this.productTypeParent.some(item => item.childrenIds.map(String).includes(idStr));
      return !exists;
    },

    /**
     * Check if all children are disable and disabled filter menu if so
     */
    checkAllDisabledFilterMenus() {
      const filters = document.querySelectorAll('.filter')

      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i]
        const filterHeader = filter.querySelector('.filter__header')
        const filterOptions = filter.querySelectorAll('.filter__options input')
        let notDisabled = false

        for (let j = 0; j < filterOptions.length; j++) {
          const filterOption = filterOptions[j]

          // @ts-ignore
          if (!filterOption.hasAttribute('data-disabled')) {
            notDisabled = true
          }
        }

        if (notDisabled) {
          filter.classList.remove('is-disabled')
        } else {
          filter.classList.add('is-disabled')
        }
      }
    },

    /**
		 * Handle filter menu open/close
		 */
    handleFilterMenu(event) {
      const parentFilter = event.target.closest('.filter');
      this.resetFilterMenus(parentFilter);

      if (!parentFilter) return;

      const filterHeader = parentFilter.querySelector('.filter__header');

      // If already active, close it
      if (parentFilter.classList.contains('is-active')) {
        parentFilter.classList.remove('is-active');
        if (filterHeader) {
          filterHeader.setAttribute('aria-expanded', 'false');
        }
        // No need to remove a click-outside listener here if you're
        // adding one only when opening (see below).
        return;
      }

      // Otherwise, open it:
      if (!parentFilter.classList.contains('is-disabled')) {
        parentFilter.classList.add('is-active');
        if (filterHeader) {
          filterHeader.setAttribute('aria-expanded', 'true');
        }
      }

      // Listen for clicks outside the .filter to close it
      const handleOutsideClick = (evt) => {
        // If the click is NOT inside parentFilter, close it
        if (!parentFilter.contains(evt.target)) {
          parentFilter.classList.remove('is-active');
          if (filterHeader) {
            filterHeader.setAttribute('aria-expanded', 'false');
          }
          // Remove this listener after closing
          document.removeEventListener('click', handleOutsideClick);
        }
      };

      // Attach the outside click listener
      document.addEventListener('click', handleOutsideClick);
    },

    /**
		 * Reset filter menus
		 */
    resetFilterMenus(passedFilter) {
      const filters = document.querySelectorAll('.filter')

      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i]
        
        if (passedFilter !== filter) {
          const filterHeader = filter.querySelector('.filter__header')
          filter.classList.remove('is-active')
  
          if (filterHeader !== null) {
            filterHeader.setAttribute('aria-expanded', 'false')
          }
        }
      }
    },

    openShortlistMenu() {
      this.$store.productShortlist.shortlistOpen = true;

      const timeoutId = setTimeout(() => {
        this.$store.productShortlist.shortlistOpen = false;
      }, 3000);

      const shortlistMenu = document.querySelector('.shortlist__items__inner');
      if (shortlistMenu) {
        shortlistMenu.addEventListener('mouseenter', () => {
          clearTimeout(timeoutId);
        });
      }
    }
	};
}
